import { useState } from "react";

import * as service from "../services/groupCreatorService";

const emptyCounts = () => ({
  total: 0,
  approved: 0,
  pending: 0,
});

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const normalizeGroupTag = (group) =>
  String(group?.groupTag ?? group?.group_tag ?? "");

const normalizeGroupsResponse = (data) => {
  const groups = Array.isArray(data)
    ? data
    : Array.isArray(data?.groups)
      ? data.groups
      : [];

  return groups;
};

const normalizeGroupResponse = (data) => data?.group ?? data ?? null;

const normalizeMembersResponse = (data) => {
  const members = Array.isArray(data)
    ? data
    : Array.isArray(data?.members)
      ? data.members
      : [];

  const approved = members.filter(
    (member) => String(member.status ?? "").toLowerCase() === "approved",
  ).length;

  const pending = members.filter(
    (member) => String(member.status ?? "").toLowerCase() === "pending",
  ).length;

  return {
    members,
    counts: {
      total: Number(data?.counts?.total ?? members.length),
      approved: Number(data?.counts?.approved ?? approved),
      pending: Number(data?.counts?.pending ?? pending),
    },
  };
};

export const useCreatorGroups = (username) => {
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState(null);

  const [members, setMembers] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [memberCounts, setMemberCounts] = useState(emptyCounts);

  const [loading, setLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);

  const [error, setError] = useState("");
  const [membersError, setMembersError] = useState("");

  const getCreatorGroups = async () => {
    if (!username) {
      throw new Error("Creator username is required.");
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.getCreatorGroups(username);

      const data = normalizeGroupsResponse(response.data);

      setGroups(data);

      return data;
    } catch (requestError) {
      setGroups([]);
      setError(getErrorMessage(requestError, "Failed to load creator groups."));
      throw requestError;
    } finally {
      setLoading(false);
    }
  };

  const getCreatorGroup = async (groupTag) => {
    if (!username || !groupTag) {
      throw new Error("Creator username and group tag are required.");
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.getCreatorGroup(groupTag, username);

      const data = normalizeGroupResponse(response.data);

      if (!data || !normalizeGroupTag(data)) {
        throw new Error("Invalid group response.");
      }

      setGroup(data);

      return data;
    } catch (requestError) {
      setGroup(null);
      setError(getErrorMessage(requestError, "Failed to load creator group."));
      throw requestError;
    } finally {
      setLoading(false);
    }
  };

  const createCreatorGroup = async (data) => {
    if (!username || !data) {
      throw new Error("Creator username and group data are required.");
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.createCreatorGroup(username, data);

      return response.data;
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "Failed to create creator group."),
      );
      throw requestError;
    } finally {
      setLoading(false);
    }
  };

  const updateCreatorGroup = async (data) => {
    if (!username || !data?.groupTag) {
      throw new Error("Creator username and group tag are required.");
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.updateCreatorGroup(
        data.groupTag,
        username,
        data,
      );

      setGroup((current) =>
        normalizeGroupTag(current) === String(data.groupTag)
          ? { ...current, ...data }
          : current,
      );

      setGroups((current) =>
        current.map((item) =>
          normalizeGroupTag(item) === String(data.groupTag)
            ? { ...item, ...data }
            : item,
        ),
      );

      return response.data;
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "Failed to update creator group."),
      );
      throw requestError;
    } finally {
      setLoading(false);
    }
  };

  const deleteCreatorGroup = async (groupTag) => {
    if (!username || !groupTag) {
      throw new Error("Creator username and group tag are required.");
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.deleteCreatorGroup(groupTag, username);

      setGroups((current) =>
        current.filter((item) => normalizeGroupTag(item) !== String(groupTag)),
      );

      setGroup((current) =>
        normalizeGroupTag(current) === String(groupTag) ? null : current,
      );

      return response.data;
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "Failed to delete creator group."),
      );
      throw requestError;
    } finally {
      setLoading(false);
    }
  };

  const getCreatorGroupMembers = async (groupTag, status = "all") => {
    if (!username || !groupTag) {
      throw new Error("Creator username and group tag are required.");
    }

    setMembersLoading(true);
    setMembersError("");

    try {
      const response = await service.getCreatorGroupMembers(
        groupTag,
        username,
        status,
      );

      const result = normalizeMembersResponse(response.data);

      setMembers(result.members);
      setMemberCounts(result.counts);
      setMemberCount(result.counts.approved);

      return result;
    } catch (requestError) {
      setMembers([]);
      setMemberCounts(emptyCounts());
      setMemberCount(0);

      setMembersError(
        getErrorMessage(requestError, "Failed to load group members."),
      );

      throw requestError;
    } finally {
      setMembersLoading(false);
    }
  };

  const getCreatorGroupMemberCount = async (groupTag) => {
    if (!username || !groupTag) {
      throw new Error("Creator username and group tag are required.");
    }

    setMembersError("");

    try {
      const response = await service.getCreatorGroupMemberCount(
        groupTag,
        username,
      );

      const data = response.data ?? {};
      const counts = data.counts ?? {};

      const approved = Number(data.memberCount ?? counts.approved ?? 0);

      const nextCounts = {
        total: Number(counts.total ?? approved),
        approved,
        pending: Number(counts.pending ?? 0),
      };

      setMemberCount(approved);
      setMemberCounts(nextCounts);

      return nextCounts;
    } catch (requestError) {
      setMembersError(
        getErrorMessage(requestError, "Failed to load member count."),
      );

      throw requestError;
    }
  };

  const refreshCreatorGroupMembers = async (groupTag) =>
    getCreatorGroupMembers(groupTag, "all");

  const updateCreatorGroupMemberStatus = async (groupTag, memberId, status) => {
    if (!username || !groupTag || memberId == null) {
      throw new Error("Group and member details are required.");
    }

    const validStatuses = ["Approved", "Declined"];

    if (!validStatuses.includes(status)) {
      throw new Error("Invalid membership status.");
    }

    setMembersError("");

    try {
      const response = await service.updateCreatorGroupMemberStatus(
        groupTag,
        username,
        memberId,
        status,
      );

      await refreshCreatorGroupMembers(groupTag);

      return response.data;
    } catch (requestError) {
      setMembersError(
        getErrorMessage(requestError, "Failed to update member status."),
      );
      throw requestError;
    }
  };

  const removeCreatorGroupMember = async (groupTag, memberId) => {
    if (!username || !groupTag || memberId == null) {
      throw new Error("Group and member details are required.");
    }

    setMembersError("");

    try {
      const response = await service.removeCreatorGroupMember(
        groupTag,
        username,
        memberId,
      );

      await refreshCreatorGroupMembers(groupTag);

      return response.data;
    } catch (requestError) {
      setMembersError(
        getErrorMessage(requestError, "Failed to remove group member."),
      );
      throw requestError;
    }
  };

  const resetCreatorGroups = () => {
    setGroups([]);
    setGroup(null);
    setMembers([]);
    setMemberCount(0);
    setMemberCounts(emptyCounts());
    setLoading(false);
    setMembersLoading(false);
    setError("");
    setMembersError("");
  };

  return {
    groups,
    group,
    members,
    memberCount,
    memberCounts,

    loading,
    membersLoading,
    error,
    membersError,

    setGroups,
    setGroup,
    setMembers,
    setMemberCount,
    setError,

    getCreatorGroups,
    getCreatorGroup,
    createCreatorGroup,
    updateCreatorGroup,
    deleteCreatorGroup,

    getCreatorGroupMembers,
    getCreatorGroupMemberCount,
    refreshCreatorGroupMembers,
    updateCreatorGroupMemberStatus,
    removeCreatorGroupMember,

    resetCreatorGroups,
  };
};

export default useCreatorGroups;
